import { useState } from "react";
import { defaultRange } from "../components/DateRangePicker";
import { DataTable, type Column } from "../components/DataTable";
import { useTableQuery } from "../api/useTableQuery";
import { QueryError } from "../components/QueryError";
import { dateTime } from "../format";

type Source = "fintrac" | "validation" | "sftp";
/** Refines `source`: a fintrac row is either FINTRAC refusing the report or
 *  our own pipeline failing to file it -- report_candidates says ERROR for
 *  both, so the API derives this from the latest submission. */
type Kind = "fintrac_rejected" | "pipeline_error" | "validation" | "sftp";

interface ValidationError { field?: string | null; code?: string | null; message: string }

interface RejectionRow {
  rejection_key: string;
  occurred_at: string;
  source: Source;
  kind?: Kind;
  subject: string | null;
  /** Shape follows `source`: a string for fintrac, ValidationError[] for
   *  validation, a code->count map for sftp. ReasonCell is the only place
   *  that narrows it. */
  detail: unknown;
  row_count: number | null;
}

const SOURCE_LABEL: Record<Source, string> = {
  fintrac: "FINTRAC",
  validation: "API",
  sftp: "SFTP",
};

/** FINTRAC refused the filed report outright; validation caught it at our own
 *  door before it ever went out; an SFTP row summarises a whole batch. */
const SOURCE_PILL: Record<Source, string> = {
  fintrac: "pill bad",
  validation: "pill warn",
  sftp: "pill mute",
};

/** The message alone is often not enough to act on: several codes (notably
 *  UNKNOWN_FIELD, "Not a recognised field…") are worded identically for every
 *  field, so without the field name the customer can't tell *which* key they
 *  misspelt. One line per error rather than a "; "-joined run-on, since a
 *  single rejected row routinely carries several. */
function ErrorList({ errors }: { errors: ValidationError[] }) {
  if (!errors?.length) return <>—</>;
  return (
    <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none" }}>
      {errors.map((e, i) => (
        <li key={i} style={{ marginBottom: i < errors.length - 1 ? 4 : 0 }}>
          {e.field && (
            <code style={{ fontFamily: "var(--mono, monospace)", fontWeight: 600 }}>
              {e.field}
            </code>
          )}
          {e.field ? ": " : ""}
          {e.message}
        </li>
      ))}
    </ul>
  );
}

/** SFTP records a code->count map per job (built by fn_write_audit_log), so
 *  the reason describes a batch rather than naming one failure. Ordered by
 *  count: the code that failed most is the one worth fixing first. */
function SftpCodes({ codes }: { codes: Record<string, number> }) {
  const entries = Object.entries(codes ?? {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <>—</>;
  return <>{entries.map(([code, n]) => `${code} ×${n}`).join(", ")}</>;
}

const KIND_LABEL: Record<string, string> = {
  fintrac_rejected: "reports rejected by FINTRAC",
  pipeline_error: "reports delayed on our side",
};

function SourcePill({ row }: { row: RejectionRow }) {
  // Our own failure is not the customer's to fix, so it must not wear
  // FINTRAC's red: the API has already replaced its text with a notice.
  if (row.kind === "pipeline_error") return <span className="pill mute">Processing</span>;
  return (
    <span className={SOURCE_PILL[row.source] ?? "pill mute"}>
      {SOURCE_LABEL[row.source] ?? row.source}
    </span>
  );
}

function ReasonCell({ row }: { row: RejectionRow }) {
  // `detail`'s shape is a contract enforced by the API, not by TypeScript --
  // `unknown` only stops a caller from using it unchecked, it doesn't stop
  // the API from sending something unexpected. A blind cast here would throw
  // during render on a shape mismatch (a bare object rendered as a JSX child,
  // or .map called on a string), and with no ErrorBoundary in this app that
  // unmounts the whole page -- the same risk format.ts's money()/dateTime()
  // already guard against for their own fields.
  if (row.source === "validation") {
    return <ErrorList errors={Array.isArray(row.detail) ? row.detail : []} />;
  }
  if (row.source === "sftp") {
    // Object.entries doesn't throw on a non-null value (including a string
    // or array -- it just yields an odd list), so the existing `?? {}` cast
    // is tolerance enough here; no runtime check needed to avoid a throw.
    return <SftpCodes codes={(row.detail as Record<string, number>) ?? {}} />;
  }
  return <>{typeof row.detail === "string" && row.detail ? row.detail : "—"}</>;
}

function SubjectCell({ row }: { row: RejectionRow }) {
  if (row.source === "sftp") {
    // A bare job UUID says nothing; the row count is what tells you how bad
    // the batch was.
    return <>job {String(row.subject ?? "").slice(0, 8)} · {row.row_count ?? 0} rows</>;
  }
  return <>{row.subject ?? "—"}</>;
}

interface RejectionsPageProps {
  /** Set only when an internal user is inside a customer's context;
   *  customer-role users are scoped by their own JWT. */
  clientId?: string;
  /** Seeded from the URL when arriving from the summary page. */
  initialFilters?: Record<string, string>;
  initialRange?: { from: string; to: string };
}

export function RejectionsPage({ clientId, initialFilters, initialRange }: RejectionsPageProps = {}) {
  const [{ from, to }, setRange] = useState(initialRange ?? defaultRange());

  // One feed, not one request per source. The old page loaded only the active
  // tab, so a customer had to click through all three to learn whether they
  // had any rejections at all.
  const q = useTableQuery<RejectionRow>("/portal/rejections",
    clientId ? { from, to, client_id: clientId } : { from, to }, 50, initialFilters);

  const columns: Column<RejectionRow>[] = [
    { header: "When", sortKey: "occurred_at", filter: { kind: "dateRange" },
      render: (r) => dateTime(r.occurred_at) },
    { header: "Source", sortKey: "source", filter: { kind: "select", param: "f_source" },
      render: (r) => <SourcePill row={r} /> },
    { header: "Subject", sortKey: "subject", render: (r) => <SubjectCell row={r} /> },
    { header: "Reason", filter: { kind: "text", param: "f_reason" },
      render: (r) => <ReasonCell row={r} /> },
  ];

  return (
    <div>
      {/* f_kind has no column of its own (it refines Source), so DataTable
          would never show it as active -- name it here, with a way out. */}
      {q.filters.f_kind && (
        <p className="rej-note">
          Showing {KIND_LABEL[q.filters.f_kind] ?? q.filters.f_kind} only ·{" "}
          <button type="button" className="link-btn" onClick={() => q.setFilter("f_kind", "")}>
            Show all
          </button>
        </p>
      )}
      {q.isLoading && <p>Loading…</p>}
      {q.error && <QueryError error={q.error} what="rejections" />}
      {/* Gated on !isLoading too, unlike ReportsPage's equivalent block: this
          page exists to answer "do I have any rejections at all", and
          DataTable's empty state reads "Nothing in this range." -- rendering
          that before the first response lands asserts something we do not yet
          know. */}
      {!q.error && !q.isLoading && (
        <DataTable
          live={q.liveBinding}
          columns={columns}
          rows={q.items}
          total={q.total}
          limit={q.limit}
          offset={q.offset}
          onPageChange={q.setOffset}
          rowKey={(r) => r.rejection_key}
          sort={q.sort}
          dir={q.dir}
          onSortChange={q.setSort}
          filters={q.filters}
          onFilterChange={q.setFilter}
          onClearFilters={q.clearFilters}
          facets={{ f_source: q.facets.source ?? [] }}
          dateRange={{
            from, to,
            // Narrowing the range can shrink `total` below the current offset,
            // which would otherwise request a page that no longer exists.
            onChange: (f, t) => { setRange({ from: f, to: t }); q.setOffset(0); },
          }}
        />
      )}
    </div>
  );
}
